import styled, { css, keyframes } from 'styled-components';

/* ───────── Page container ───────── */

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .titles h1 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin: 0;
  }

  .titles .subtitle {
    display: block;
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  margin-bottom: 4px;
  font-family: inherit;

  &:hover {
    color: #006633;
  }
`;

/* ───────── Stepper ───────── */

export const Stepper = styled.ol`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  list-style: none;
  margin: 0;

  @media (max-width: 768px) {
    gap: 4px;
    padding: 12px;
  }
`;

export const StepItem = styled.li<{ $state: 'done' | 'active' | 'idle' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;

  &:not(:last-child)::after {
    content: '';
    flex: 1;
    height: 2px;
    border-radius: 2px;
    background: ${({ $state }) => ($state === 'done' ? '#006633' : '#e2e8f0')};
    transition: background 0.2s ease;
  }

  .indicator {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    transition: all 0.2s ease;

    ${({ $state }) => {
      if ($state === 'done') {
        return css`
          background: #006633;
          color: #ffffff;
        `;
      }
      if ($state === 'active') {
        return css`
          background: #e6f3ec;
          color: #006633;
          box-shadow: 0 0 0 4px rgba(0, 102, 51, 0.12);
        `;
      }
      return css`
        background: #f1f5f9;
        color: #94a3b8;
      `;
    }}
  }

  .label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    .index {
      font-size: 10px;
      font-weight: 700;
      color: #94a3b8;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .name {
      font-size: 13px;
      font-weight: ${({ $state }) => ($state === 'active' ? 700 : 600)};
      color: ${({ $state }) => ($state === 'idle' ? '#94a3b8' : '#0f172a')};
      letter-spacing: -0.01em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  @media (max-width: 768px) {
    .label .name {
      display: none;
    }
    .label .index {
      display: none;
    }
  }
`;

/* ───────── Card ───────── */

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .subtitle {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
`;

/* ───────── Upload dropzone ───────── */

export const Dropzone = styled.div<{ $active?: boolean; $hasFile?: boolean }>`
  border: 2px dashed
    ${({ $active, $hasFile }) => {
      if ($active) return '#006633';
      if ($hasFile) return '#c7e4d5';
      return '#e2e8f0';
    }};
  background: ${({ $active, $hasFile }) => {
    if ($active) return '#f0f9f3';
    if ($hasFile) return '#f7fcf9';
    return '#fafbfc';
  }};
  border-radius: 14px;
  padding: 36px 24px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #006633;
    background: #f7fcf9;
  }

  .icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    margin: 0 auto 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ $hasFile }) => ($hasFile ? '#006633' : '#e6f3ec')};
    color: ${({ $hasFile }) => ($hasFile ? '#ffffff' : '#006633')};
    transition: all 0.2s ease;
  }

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }

  .hint {
    font-size: 12.5px;
    color: #64748b;
    font-weight: 500;
  }

  .browse {
    color: #006633;
    font-weight: 700;
    text-decoration: underline;
  }
`;

export const FileBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;

  .icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e6f3ec;
    color: #006633;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .size {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
  }

  .remove {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    transition: all 0.15s ease;

    &:hover {
      background: #fef2f2;
      color: #b91c1c;
    }
  }
`;

export const TemplateBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fff9f1;
  border: 1px solid #fcd9b0;
  border-radius: 12px;

  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #fff1e0;
    color: #f38725;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .hint {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
    margin-top: 2px;
  }
`;

export const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #fcd9b0;
  color: #c76a13;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background: #f38725;
    border-color: #f38725;
    color: #ffffff;
    text-decoration: none;
  }
`;

/* ───────── Summary stats ───────── */

export const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

type Tone = 'neutral' | 'success' | 'warning' | 'danger';

const SUMMARY_TONE: Record<
  Tone,
  { bg: string; border: string; color: string }
> = {
  neutral: { bg: '#ffffff', border: '#eef2f7', color: '#0f172a' },
  success: { bg: '#f1faf4', border: '#c7e4d5', color: '#006633' },
  warning: { bg: '#fff9f1', border: '#fcd9b0', color: '#c76a13' },
  danger: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
};

export const SummaryTile = styled.div<{ $tone: Tone }>`
  padding: 14px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  ${({ $tone }) => css`
    background: ${SUMMARY_TONE[$tone].bg};
    border: 1px solid ${SUMMARY_TONE[$tone].border};
  `}

  .icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #ffffff;
    ${({ $tone }) => css`
      color: ${SUMMARY_TONE[$tone].color};
    `}
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .label {
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-top: 4px;
  }
`;

/* ───────── Review table ───────── */

export const ReviewTable = styled.div`
  border: 1px solid #eef2f7;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
`;

export const ReviewTableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1.6fr 1.2fr 1fr 1fr 1fr 1.4fr;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const ReviewTableBody = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

export const ReviewRow = styled.div<{ $invalid?: boolean }>`
  display: grid;
  grid-template-columns: 60px 1.6fr 1.2fr 1fr 1fr 1fr 1.4fr;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  align-items: center;
  background: ${({ $invalid }) => ($invalid ? '#fff8f8' : '#ffffff')};

  &:last-child {
    border-bottom: none;
  }

  .rowNum {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #0f172a;
    font-weight: 500;
  }

  .cell.muted {
    color: #94a3b8;
    font-style: italic;
  }

  .errors {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .error-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    background: #fee2e2;
    color: #b91c1c;
    max-width: fit-content;
  }

  .ok-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    background: #e6f3ec;
    color: #006633;
    max-width: fit-content;
  }
`;

/* ───────── Footer actions ───────── */

export const StepFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 6px;
  border-top: 1px dashed #e2e8f0;

  .left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  .right {
    display: inline-flex;
    gap: 10px;
  }
`;

/* ───────── Buttons ───────── */

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BTN_MAP: Record<
  BtnVariant,
  { bg: string; color: string; border: string; hoverBg: string }
> = {
  primary: {
    bg: '#006633',
    color: '#ffffff',
    border: '#006633',
    hoverBg: '#00451f',
  },
  secondary: {
    bg: '#ffffff',
    color: '#0f172a',
    border: '#e2e8f0',
    hoverBg: '#f8fafc',
  },
  ghost: {
    bg: 'transparent',
    color: '#64748b',
    border: 'transparent',
    hoverBg: '#f1f5f9',
  },
  danger: {
    bg: '#ffffff',
    color: '#b91c1c',
    border: '#fecaca',
    hoverBg: '#fef2f2',
  },
};

export const Button = styled.button<{ $variant?: BtnVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  ${({ $variant = 'primary' }) => css`
    background: ${BTN_MAP[$variant].bg};
    color: ${BTN_MAP[$variant].color};
    border: 1px solid ${BTN_MAP[$variant].border};

    &:hover:not(:disabled) {
      background: ${BTN_MAP[$variant].hoverBg};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ───────── Checkbox (declaração) ───────── */

export const DeclarationRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fafbfc;

  &:hover {
    border-color: #c7e4d5;
    background: #f7fcf9;
  }

  input {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: #006633;
  }

  .text {
    font-size: 12.5px;
    color: #334155;
    font-weight: 500;
    line-height: 1.45;
  }

  .text strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

/* ───────── Status alerts ───────── */

export const Alert = styled.div<{ $tone: Tone }>`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 500;
  ${({ $tone }) => css`
    background: ${SUMMARY_TONE[$tone].bg};
    border: 1px solid ${SUMMARY_TONE[$tone].border};
    color: ${SUMMARY_TONE[$tone].color};
  `}

  .icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  .body {
    flex: 1;
    color: #334155;
    line-height: 1.45;
  }

  strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

/* ───────── Select wrapper (to integrate react-select) ───────── */

export const SelectWrapper = styled.div`
  .react-select__control {
    border-radius: 10px;
    border-color: #e2e8f0;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: #006633;
    }
  }

  .react-select__control--is-focused {
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }

  .react-select__placeholder {
    color: #94a3b8;
    font-size: 13px;
  }

  .react-select__single-value {
    color: #0f172a;
    font-weight: 600;
    font-size: 13px;
  }

  .react-select__option--is-focused {
    background: #f1faf4;
  }

  .react-select__option--is-selected {
    background: #006633;
  }
`;

/* ───────── Result step ───────── */

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

export const ResultBlock = styled.div<{ $tone: Tone }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;

  .icon-wrap {
    width: 72px;
    height: 72px;
    border-radius: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    animation: ${pulse} 2s ease-in-out infinite;
    ${({ $tone }) => css`
      background: ${SUMMARY_TONE[$tone].bg};
      color: ${SUMMARY_TONE[$tone].color};
      border: 1px solid ${SUMMARY_TONE[$tone].border};
    `}
  }

  h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
    max-width: 420px;
  }
`;

export const ErrorList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fef7f7;

  li {
    padding: 10px 14px;
    font-size: 12.5px;
    color: #7f1d1d;
    font-weight: 500;
    border-bottom: 1px solid #fee2e2;
    line-height: 1.45;

    &:last-child {
      border-bottom: none;
    }
  }
`;
